import { z } from "zod";

/**
 * Lenient field parsers, matching how the Flutter models read the wire.
 *
 * The backend is ours, but a screen must never go blank because one field in
 * one row arrived odd. Each helper reads the value it can and falls back to
 * a neutral default otherwise; the strict cases (a missing chart, a plan
 * with no allowance) are handled explicitly where they matter.
 */

/** A trimmed string, or "" when absent or not a string. */
export const text = (fallback = "") => z.string().trim().catch(fallback);

/** A string that stays undefined when absent, trimmed when present. */
export const optionalText = () =>
  z
    .string()
    .trim()
    .nullish()
    .transform((value) => (value ? value : undefined))
    .catch(undefined);

/** An integer; numeric strings are accepted, anything else is the fallback. */
export const int = (fallback = 0) =>
  z.coerce
    .number()
    .transform((value) => Math.trunc(value))
    .catch(fallback);

/** An integer that stays undefined when null or absent. */
export const optionalInt = () =>
  z
    .number()
    .nullish()
    .transform((value) => (typeof value === "number" ? Math.trunc(value) : undefined))
    .catch(undefined);

/** A real number; numeric strings accepted. */
export const real = (fallback = 0) => z.coerce.number().catch(fallback);

/** True only for a literal `true`. */
export const flag = (fallback = false) => z.boolean().catch(fallback);

/** A list of non-empty trimmed strings; anything else is dropped. */
export const strings = () =>
  z
    .array(z.unknown())
    .catch([])
    .transform((items) =>
      items.flatMap((item) =>
        typeof item === "string" && item.trim() ? [item.trim()] : [],
      ),
    );

/**
 * A list where each malformed item is skipped instead of failing the whole
 * list — one bad placement must not hide the other eight.
 */
export const lenientArray = <T extends z.ZodType>(schema: T) =>
  z
    .array(z.unknown())
    .catch([])
    .transform((items) =>
      items.flatMap((item) => {
        const result = schema.safeParse(item);
        return result.success ? [result.data as z.output<T>] : [];
      }),
    );

/** An ISO timestamp as a Date, or undefined when absent or unparseable. */
export const isoDate = () =>
  z
    .string()
    .nullish()
    .transform((value) => {
      if (!value) return undefined;
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? undefined : date;
    })
    .catch(undefined);

/** Parses a response body against a schema, or throws a bad-response error. */
export function parseBody<T extends z.ZodType>(schema: T, body: unknown): z.output<T> {
  const result = schema.safeParse(body);
  if (!result.success) {
    // Imported lazily by callers through ApiError to avoid a cycle here.
    throw new BadShape(result.error.message);
  }
  return result.data as z.output<T>;
}

/** Raised when a body cannot be read as the schema; the client maps it. */
export class BadShape extends Error {
  constructor(detail: string) {
    super(detail);
    this.name = "BadShape";
  }
}
